import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { ArrowLeft, LogOut } from 'lucide-react';
import { formatTitle } from '@/lib/typography';
import { PlaybookPageV2 } from '@/components/playbook/PlaybookPageV2';
import { TimelinePage } from '@/components/playbook/TimelinePage';
import { MindmapPage } from '@/components/playbook/MindmapPage';
import { OptimizerPage } from '@/components/playbook/OptimizerPage';
import { KeywordsPage } from '@/components/playbook/KeywordsPage';
import { ReviewPage } from '@/components/playbook/ReviewPage';

const SESSION_KEY = 'admin_authenticated';

const Tabs = ({ 
  tabs, 
  current, 
  onChange 
}: { 
  tabs: Array<{ id: string; label: string }>; 
  current: string; 
  onChange: (id: string) => void;
}) => (
  <div className="flex flex-wrap gap-2">
    {tabs.map((t) => (
      <Button 
        key={t.id} 
        variant={current === t.id ? "default" : "outline"} 
        size="sm" 
        onClick={() => onChange(t.id)}
      >
        {t.label}
      </Button>
    ))}
  </div>
);

const SectionTitle = ({ 
  icon, 
  children 
}: { 
  icon: React.ReactNode; 
  children: React.ReactNode;
}) => (
  <h2 className="text-xl font-semibold flex items-center gap-2 mb-3">
    {icon}
    {children}
  </h2>
);

export default function AdminPlaybookAmazonAds() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("PlaybookV2");

  useEffect(() => {
    const isAuth = sessionStorage.getItem(SESSION_KEY) === 'true';
    if (!isAuth) {
      navigate('/admin/auth');
    }
  }, [navigate]);

  const handleLogout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    navigate('/admin/auth');
  };

  const tabs = [
    { id: "PlaybookV2", label: "Playbook 🔥" },
    { id: "Timeline", label: "Timeline" },
    { id: "Mindmap", label: "Mindmap" },
    { id: "Optimizer", label: "Optimizer" },
    { id: "Keywords", label: "Keywords" },
    { id: "Review", label: "Review" },
  ];

  return (
    <>
      <Helmet>
        <title>{formatTitle("Cosmos Wave — Amazon Ads Master Playbook+ 🇫🇷")}</title>
        <meta name="description" content="Playbook complet Amazon Ads pour la France — Stratégie, optimisation, générateur de mots-clés et revue IA" />
      </Helmet>

      <div className="min-h-screen bg-background p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Link to="/admin/simulateurs">
                  <Button variant="ghost" size="sm">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Retour
                  </Button>
                </Link>
                <Button variant="ghost" size="sm" onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Déconnexion
                </Button>
              </div>
              <h1 className="text-2xl font-bold">
                {formatTitle("Cosmos Wave — Amazon Ads Master Playbook+ 🇫🇷")}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Suite — Playbook (stratégie), Timeline (séquence), Mindmap (panorama), Optimizer (exécution guidée), Keywords (générateur), Review (revue IA)
              </p>
            </div>
            <div className="shrink-0">
              <Tabs tabs={tabs} current={tab} onChange={setTab} />
            </div>
          </div>

          {/* Content */}
          <div className="space-y-4">
            {tab === "PlaybookV2" && (
              <div>
                <SectionTitle icon={<span>🚀</span>}>
                  {formatTitle("Playbook V2 — 100% réactif — Tous budgets — Garde-fous retail")}
                </SectionTitle>
                <PlaybookPageV2 />
              </div>
            )}

            {tab === "Timeline" && (
              <div>
                <SectionTitle icon={<span>📅</span>}>
                  {formatTitle("Timeline — Séquence et saisonnalité France")}
                </SectionTitle>
                <TimelinePage />
              </div>
            )}

            {tab === "Mindmap" && (
              <div>
                <SectionTitle icon={<span>🧠</span>}>
                  {formatTitle("Mindmap — Panorama (arborescence)")}
                </SectionTitle>
                <MindmapPage />
              </div>
            )}

            {tab === "Optimizer" && (
              <div>
                <SectionTitle icon={<span>⚙️</span>}>
                  {formatTitle("Optimizer — Assistant Pro (guidé pas-à-pas)")}
                </SectionTitle>
                <OptimizerPage />
              </div>
            )}

            {tab === "Keywords" && (
              <div>
                <SectionTitle icon={<span>🔑</span>}>
                  {formatTitle("Keywords — Générateur dynamique")}
                </SectionTitle>
                <KeywordsPage />
              </div>
            )}

            {tab === "Review" && (
              <div>
                <SectionTitle icon={<span>📊</span>}>
                  {formatTitle("Review — Revue hebdo IA")}
                </SectionTitle>
                <ReviewPage />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
